import { EntityEvents } from '@common/user-analytics';
import { NestedData } from '@prophecy/interfaces/generic';
import { Stack } from '@prophecy/ui-v3/Layout';
import { message, toast } from '@prophecy/ui-v3/LegacyToast';
import { useEffect } from 'react';
import { FileRejection, ErrorCode as FileRejectionErrorCode } from 'react-dropzone';

import { useMixPanel } from '../../context/mixpanel/context';
import { uploadFilesToTranspile } from '../../data/apis/api';
import { useRestMutation } from '../../data/util';
import { QueryKey } from './config';
import { MAX_ALLOWED_FILES, MAX_FILE_SIZE } from './constants';
import { FolderUploadTypes } from './Import/steps/types';
import { UseUploadMutation } from './UseUploadMutationType';

export function useUploadMutation(
  transpilerId: string,
  setUpdatePath: (update: string) => void,
  setPercentage: (percentage: number) => void,
  resetUploadProgress: () => void
): UseUploadMutation {
  const { track } = useMixPanel();
  useEffect(() => {
    track(EntityEvents.transpile.home.chooseEntities, {
      entity: QueryKey,
      url: window.location.href
    });
  }, [track]);

  const uploadMutation = useRestMutation(
    (fileFormData) => {
      track(EntityEvents.transpile.home.importStarted, { transpilerId });
      return uploadFilesToTranspile(transpilerId, fileFormData, setPercentage);
    },
    {
      retry: false,
      sentryTag: 'transpiler.uploadFiles'
    }
  );

  const uploadFiles = async (files: NestedData[], fileRejection: FileRejection[]) => {
    if (fileRejection?.length > 0) {
      if (fileRejection[0].errors.some((error) => error.code === FileRejectionErrorCode.TooManyFiles)) {
        message.error({ content: `File count exceeded the max allowed count of ${MAX_ALLOWED_FILES}.`, width: 450 });
        return;
      }

      const fileSizeErrors = fileRejection
        .filter(({ errors }) => {
          return errors.some((error) => error.code === FileRejectionErrorCode.FileTooLarge);
        })
        .map(({ file }) => `${file.name} (${Math.round(file.size / 1000 / 1000)}MB)`);

      if (fileSizeErrors.length) {
        message.error({
          width: 550,
          content: `File size exceeded the max allowed size of ${MAX_FILE_SIZE / 1000 / 1000}MB`,
          detail: (
            <>
              {fileSizeErrors.map((error, index) => {
                return (
                  <Stack key={index} width='420px'>
                    - {error}
                  </Stack>
                );
              })}
            </>
          )
        });
        return;
      }
    }
    const data = new FormData();
    files.forEach((file) => {
      data.append('file', file as Blob, file.name);
    });
    try {
      setPercentage(0);
      await uploadMutation.mutateAsync(data);
    } catch (e) {
      resetUploadProgress();
      setUpdatePath(FolderUploadTypes.error);
      toast.error({ content: (e as { message: string })?.message || 'Some error occurred' });
    }
  };

  return { uploadFiles, uploadMutation, setUpdatePath };
}
